/**
 * AI:
 * Status: 'confirmed'
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Manual object-search tab. Lets the operator pick a record structure,
 *         search the matching object type via AutoComplete and assign the chosen
 *         object, which then triggers an automatic record-level re-lookup.
 *         Core workflow of the EI multi-stage user story.
 */
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges
} from '@angular/core';
import {Subject} from 'rxjs';
import {switchMap, takeUntil} from 'rxjs/operators';
import {EiImportRecordDto} from '../../../../../../../../model/dto/entity/ei-import-record.dto';
import {EiImportRecordStructureDto} from '../../../../../../../../model/dto/entity/ei-import-record-structure.dto';
import {EiImportRecordService} from '../../../../../../../../service/api/ei-import-record.service';
import {EiImportReviewService} from '../../../../../../../../service/domain/ei-import-review.service';
import {MvsCoreService} from '../../../../../../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../../../../../../core/shared/service/message/mvs-message.service';
import {FilterCriteria} from '../../../../../../../../../../core/shared/filter/api/filter.criteria';
import {ObjectRequestList} from '../../../../../../../../../../core/shared/dto/object/request/object-request-list';
import {DtoList} from '../../../../../../../../../../core/shared/dto/dto.list';
import {DtoDetail} from '../../../../../../../../../../core/shared/dto/dto.detail';
import {
    EiImportRecordStructureAssignmentStatus
} from '../../../../../../../../enum/ei-import-record-structure-assignment-status.enum';
import {EiImportReResolveResultDto} from '../../../../../../../../model/dto/api/ei-import-re-resolve-result.dto';

/**
 * Options shown in the structure dropdown.
 */
export interface StructureOption {
    id: number;
    objectTypeName: string;
    label: string;
    assignmentStatusLabel: string;
    /** DTO alias (e.g. 'cm.Contract'), pulled from dto-service map when available. */
    objectTypeAlias: string | null;
    /** Current object id (if any). */
    objectId?: number;
}

/**
 * Autocomplete suggestion entry.
 */
interface SearchSuggestion {
    id: number;
    displayName: string;
    raw: DtoDetail;
}

/**
 * Simple key/value preview entry.
 */
interface PreviewField {
    key: string;
    label: string;
    value: string;
}

@Component({
    selector: 'ei-tooling-object-search-tab',
    templateUrl: './ei-tooling-object-search-tab.component.html',
    styleUrls: ['./ei-tooling-object-search-tab.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EiToolingObjectSearchTabComponent implements OnChanges, OnDestroy {

    @Input() record: EiImportRecordDto | null = null;
    @Input() recordStructures: EiImportRecordStructureDto[] = [];
    @Input() preselectedStructureId: number | null = null;

    @Output() onAssignedAndReLookup: EventEmitter<{ structureId: number, objectId: number }> =
        new EventEmitter<{ structureId: number, objectId: number }>();

    /** Structure options derived from recordStructures. */
    structureOptions: StructureOption[] = [];
    selectedStructure: StructureOption | null = null;

    /** Autocomplete state. */
    selectedObject: SearchSuggestion | null = null;
    filteredData: SearchSuggestion[] = [];
    searchLoading: boolean = false;

    /** Preview of the chosen object. */
    previewObject: SearchSuggestion | null = null;
    previewFields: PreviewField[] = [];

    /** In-flight flag for assign+re-lookup. */
    assigning: boolean = false;

    /** Warning shown when the active object type has no CRUD service. */
    serviceMissingWarning: string | null = null;

    private readonly destroy$: Subject<void> = new Subject<void>();

    constructor(
        private readonly mvsCoreService: MvsCoreService,
        private readonly recordService: EiImportRecordService,
        private readonly reviewService: EiImportReviewService,
        private readonly messageService: MvsMessageService,
        private readonly cdr: ChangeDetectorRef,
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['recordStructures']) {
            this.rebuildStructureOptions();
            this.applyPreselection();
        } else if (changes['preselectedStructureId']) {
            this.applyPreselection();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Rebuilds `structureOptions` from the current recordStructures input.
     */
    private rebuildStructureOptions(): void {
        const structures = this.recordStructures ?? [];
        this.structureOptions = structures.map(s => ({
            id: s.id,
            objectTypeName: s.objectTypeDtoName ?? 'Unbekannt',
            label: (s.objectTypeDtoName ?? 'Unbekannt') + ' (Struktur #' + s.id + ')',
            assignmentStatusLabel: this.assignmentStatusLabel(s.assignmentStatus),
            objectTypeAlias: this.resolveAliasFromStructure(s),
            objectId: s.objectId,
        }));
        this.cdr.markForCheck();
    }

    /**
     * Selects the preselected structure id if available.
     */
    private applyPreselection(): void {
        if (this.preselectedStructureId != null) {
            const found = this.structureOptions.find(s => s.id === this.preselectedStructureId);
            if (found) {
                this.selectedStructure = found;
                this.onStructureChange();
            }
        }
    }

    /**
     * Resolves the DTO alias for a given structure (e.g. 'cm.Contract').
     *
     * The alias is sourced from `objectTypeDto.alias`, which is populated by requesting
     * the `objectType` sub-DTO via ObjectRequestComplex in the parent component's
     * `loadStructures()`. If the sub-DTO is absent the tab shows the "no alias" warning.
     */
    private resolveAliasFromStructure(structure: EiImportRecordStructureDto): string | null {
        if (structure?.objectTypeDto?.alias) {
            return structure.objectTypeDto.alias;
        }
        return null;
    }

    /**
     * Called when the operator changes the target structure.
     */
    onStructureChange(): void {
        this.selectedObject = null;
        this.filteredData = [];
        this.previewObject = null;
        this.previewFields = [];
        this.serviceMissingWarning = null;

        if (this.selectedStructure) {
            const alias = this.selectedStructure.objectTypeAlias;
            if (!alias) {
                this.serviceMissingWarning =
                    'Für diesen Objekttyp ist keine Such-Schnittstelle registriert.';
            }
        }
        this.cdr.markForCheck();
    }

    /**
     * AutoComplete filter — queries the CRUD service for the active object type.
     */
    filterObjects(event: { query: string }): void {
        if (!this.selectedStructure?.objectTypeAlias) {
            this.filteredData = [];
            return;
        }
        const alias = this.selectedStructure.objectTypeAlias;
        const crudService = this.mvsCoreService.getCrudService(alias);
        if (!crudService) {
            this.serviceMissingWarning =
                'Für diesen Objekttyp ist keine Such-Schnittstelle registriert.';
            this.filteredData = [];
            this.cdr.markForCheck();
            return;
        }

        const pattern = '%' + (event?.query ?? '').trim() + '%';
        const filter = [
            FilterCriteria.create('name', FilterCriteria.cOperatorContainsPattern, pattern),
        ];
        const request = ObjectRequestList.createBasic(false, filter, []);

        this.searchLoading = true;
        crudService.list(request)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (list: DtoList<DtoDetail>) => {
                    const entries: Array<DtoDetail> = list?.entries ?? [];
                    this.filteredData = entries.map(e => ({
                        id: e.id,
                        displayName: e.name ?? ('#' + e.id),
                        raw: e
                    }));
                    this.searchLoading = false;
                    this.cdr.markForCheck();
                },
                error: err => {
                    console.error('Suche fehlgeschlagen', err);
                    this.filteredData = [];
                    this.searchLoading = false;
                    this.cdr.markForCheck();
                }
            });
    }

    /**
     * Shows the preview card for a selected suggestion.
     */
    onPreviewSelect(selection: SearchSuggestion): void {
        if (!selection) {
            return;
        }
        this.previewObject = selection;
        this.previewFields = this.buildPreviewFields(selection.raw as unknown as Record<string, unknown>);
        this.cdr.markForCheck();
    }

    /**
     * Builds a small preview-field list from an arbitrary DTO.
     */
    private buildPreviewFields(obj: Record<string, unknown> | null): PreviewField[] {
        if (!obj) {
            return [];
        }
        const candidates: Array<[string, string]> = [
            ['id', 'ID'],
            ['name', 'Name'],
            ['displayName', 'Anzeigename'],
            ['status', 'Status'],
            ['createdDate', 'Angelegt'],
            ['lastModifiedDate', 'Zuletzt geändert'],
        ];
        const result: PreviewField[] = [];
        for (const [key, label] of candidates) {
            const val = obj?.[key];
            if (val != null && val !== '') {
                result.push({key, label, value: String(val)});
            }
        }
        return result;
    }

    /**
     * Clears the preview card.
     */
    cancelPreview(): void {
        this.previewObject = null;
        this.previewFields = [];
        this.selectedObject = null;
        this.cdr.markForCheck();
    }

    /**
     * Resets the entire search form (after successful assign).
     */
    private resetForm(): void {
        this.selectedObject = null;
        this.previewObject = null;
        this.previewFields = [];
        this.filteredData = [];
        this.cdr.markForCheck();
    }

    /**
     * Executes the core workflow: assigns the chosen object to the structure
     * and then triggers an automatic record-level re-lookup.
     */
    assignAndReLookup(): void {
        if (!this.record?.id || !this.selectedStructure || !this.previewObject) {
            return;
        }
        const recordId = this.record.id;
        const structureId = this.selectedStructure.id;
        const objectId = this.previewObject.id;
        const importId = this.record.eiImportDtoId;

        this.assigning = true;
        this.cdr.markForCheck();

        this.recordService.assignEntity(recordId, structureId, objectId)
            .pipe(
                switchMap(() => {
                    if (importId == null) {
                        // Still resolve happily — no import id to trigger batch re-lookup.
                        return Promise.resolve<EiImportReResolveResultDto>({triggeredCount: 0, stillPendingCount: 0});
                    }
                    return this.reviewService.reResolveRecords(importId, [recordId]);
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: () => {
                    this.messageService.showSuccessMessage(
                        'Zuweisung erfolgreich',
                        'Objekt zugewiesen und Re-Lookup wurde angestoßen.'
                    );
                    this.onAssignedAndReLookup.emit({structureId, objectId});
                    this.resetForm();
                    this.assigning = false;
                    this.cdr.markForCheck();
                },
                error: err => {
                    console.error('Zuweisung fehlgeschlagen', err);
                    this.messageService.showErrorMessage(
                        'Fehler',
                        'Zuweisung fehlgeschlagen: ' + (err?.message ?? 'Unbekannter Fehler')
                    );
                    this.assigning = false;
                    this.cdr.markForCheck();
                }
            });
    }

    /**
     * Returns a German label for the assignment status enum value.
     */
    private assignmentStatusLabel(status: EiImportRecordStructureAssignmentStatus): string {
        switch (status) {
            case EiImportRecordStructureAssignmentStatus.NOT_FOUND: return 'Nicht gefunden';
            case EiImportRecordStructureAssignmentStatus.AUTO_FOUND: return 'Automatisch gefunden';
            case EiImportRecordStructureAssignmentStatus.AUTO_CREATION: return 'Automatisch angelegt';
            case EiImportRecordStructureAssignmentStatus.MANUAL_ASSIGNMENT: return 'Manuell zugewiesen';
            case EiImportRecordStructureAssignmentStatus.MANUAL_CREATION: return 'Manuell angelegt';
            case EiImportRecordStructureAssignmentStatus.QL_FOUND: return 'QL-Treffer';
            case EiImportRecordStructureAssignmentStatus.SCOPED_FOUND: return 'Scoped-Treffer';
            default: return 'Unbekannt';
        }
    }
}
